/**
 * Sophyteca - Favoritos
 * Página de livros favoritados pelo usuário
 * Data: 01 de Novembro de 2025
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Favorito {
  id: number;
  livro_id: number;
  titulo: string;
  autor: string;
  url_capa?: string;
  tipo_fonte: 'proprio' | 'externo';
  url_externa?: string;
  arquivo_url?: string;
  anotacoes?: string;
  lido: boolean;
  criado_em: string;
}

export default function SophytecaFavoritos() {
  const navigate = useNavigate();
  const [favoritos, setFavoritos] = useState<Favorito[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarFavoritos();
  }, []);

  const carregarFavoritos = async () => {
    try {
      const response = await api.get('/sophyteca/favoritos');
      
      if (response.data.sucesso) {
        setFavoritos(response.data.dados);
      }
    } catch (error: any) {
      console.error('[SOPHYTECA] Erro carregar favoritos:', error);
      alert('Erro ao carregar favoritos');
    } finally {
      setLoading(false);
    }
  };

  const handleRemover = async (livroId: number) => {
    if (!confirm('Deseja remover este livro dos favoritos?')) return;

    try {
      const response = await api.delete(`/sophyteca/favoritos/${livroId}`);
      
      if (response.data.sucesso) {
        setFavoritos(favoritos.filter(f => f.livro_id !== livroId));
      }
    } catch (error: any) {
      console.error('[SOPHYTECA] Erro remover favorito:', error);
      alert('Erro ao remover favorito');
    }
  };

  const handleAcessar = (favorito: Favorito) => {
    if (favorito.tipo_fonte === 'externo' && favorito.url_externa) {
      window.open(favorito.url_externa, '_blank');
    } else if (favorito.arquivo_url) {
      window.open(favorito.arquivo_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-12">
              <h1>
                <i className="fas fa-star text-warning"></i> Meus Favoritos
              </h1>
              <button 
                className="btn btn-outline-secondary mt-2"
                onClick={() => navigate('/sophyteca')}
              >
                <i className="fas fa-arrow-left"></i> Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          {favoritos.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-star fa-4x text-muted mb-3"></i>
                <h5 className="text-muted">Nenhum favorito ainda</h5>
                <p className="text-muted">
                  Adicione livros aos favoritos para acessá-los rapidamente aqui
                </p>
              </div>
            </div>
          ) : (
            <div className="row">
              {favoritos.map((favorito) => (
                <div key={favorito.id} className="col-md-4 mb-4">
                  <div className="card h-100 shadow-sm">
                    {favorito.url_capa ? (
                      <img 
                        src={favorito.url_capa} 
                        className="card-img-top" 
                        alt={favorito.titulo}
                        style={{ height: '200px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="card-img-top d-flex align-items-center justify-content-center bg-light"
                        style={{ height: '200px' }}
                      >
                        <i className="fas fa-book fa-4x text-muted"></i>
                      </div>
                    )}
                    
                    <div className="card-body">
                      <h6 className="card-title">{favorito.titulo}</h6>
                      <p className="card-text text-muted small">
                        <i className="fas fa-user"></i> {favorito.autor}
                      </p>
                      
                      {favorito.anotacoes && (
                        <p className="card-text small">
                          <strong>Anotações:</strong> {favorito.anotacoes}
                        </p>
                      )}
                      
                      <small className="text-muted">
                        Adicionado em {new Date(favorito.criado_em).toLocaleDateString('pt-BR')}
                      </small>
                    </div>
                    
                    <div className="card-footer bg-white">
                      <div className="d-grid gap-2">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleAcessar(favorito)}
                        >
                          <i className="fas fa-book-open"></i> Acessar
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleRemover(favorito.livro_id)}
                        >
                          <i className="fas fa-trash"></i> Remover
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
